import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "font-inter text-chip font-medium inline-flex items-center justify-center rounded-full border px-2 py-1 w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-badge-neutral-border bg-badge-neutral text-foreground-secondary [a&]:hover:brightness-95",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border-badge-neutral-border bg-badge-neutral text-foreground-secondary [a&]:hover:brightness-95",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const resolvedClassName = cn(badgeVariants({ variant }), className)
  const slotProps = props as React.ComponentProps<typeof Slot>

  if (asChild) {
    return (
      <Slot
        data-slot="badge"
        className={resolvedClassName}
        {...slotProps}
      />
    )
  }

  return (
    <span
      data-slot="badge"
      className={resolvedClassName}
      {...props}
    />
  )
}

export { Badge }
